import { Link, usePage } from '@inertiajs/react'
import { useState, useRef, useEffect } from 'react'

interface BorrowerUser {
  id: number
  email: string
  is_pro?: boolean
}

interface Props {
  variant: 'public' | 'authenticated'
}

function DebtLensLogo() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-dl-accent/15 flex items-center justify-center">
        <svg className="w-4.5 h-4.5 text-dl-accent" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        </svg>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-lg font-bold text-white tracking-tight">
          Debt<span className="text-dl-accent">Lens</span>
        </span>
        <span className="text-[10px] text-dl-muted font-medium">by LendSolo</span>
      </div>
    </div>
  )
}

export default function DebtLensHeader({ variant }: Props) {
  const props = usePage().props
  const currentBorrower = (props as Record<string, unknown>).current_borrower_user as BorrowerUser | null
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''

  useEffect(() => {
    if (!menuOpen) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  const navItems = [
    { name: 'Dashboard', href: '/my-loans/dashboard' },
    { name: 'My Loans', href: '/my-loans/dashboard' },
    { name: 'Settings', href: '/my-loans/billing' },
  ]

  if (variant === 'public') {
    return (
      <header
        className="w-full"
        style={{ background: 'linear-gradient(135deg, var(--color-dl-chrome-from) 0%, var(--color-dl-chrome-to) 100%)' }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link href="/my-loans">
            <DebtLensLogo />
          </Link>
          <div className="flex items-center gap-3">
            <Link
              href="/my-loans/login"
              className="text-sm text-dl-muted hover:text-white transition-colors"
            >
              Log in
            </Link>
          </div>
        </div>
      </header>
    )
  }

  return (
    <header
      className="w-full"
      style={{ background: 'linear-gradient(135deg, var(--color-dl-chrome-from) 0%, var(--color-dl-chrome-to) 100%)' }}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link href="/my-loans/dashboard">
            <DebtLensLogo />
          </Link>
          <nav className="hidden sm:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = currentPath === item.href || (item.name === 'Dashboard' && currentPath === '/my-loans/dashboard')
              return (
                <Link
                  key={item.name}
                  href={item.href}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-dl-accent bg-dl-accent/10'
                      : 'text-dl-muted hover:text-white hover:bg-white/5'
                  }`}
                >
                  {item.name}
                </Link>
              )
            })}
          </nav>
        </div>

        {currentBorrower && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center gap-2"
              aria-label="User menu"
              aria-expanded={menuOpen}
            >
              <div className="w-8 h-8 rounded-full bg-dl-accent/20 flex items-center justify-center">
                <span className="text-dl-accent font-semibold text-sm">
                  {currentBorrower.email.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="hidden sm:inline text-sm text-dl-muted">
                {currentBorrower.email}
              </span>
            </button>

            {menuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <Link
                  href="/my-loans/billing"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Billing
                </Link>
                <Link
                  href="/my-loans/logout"
                  method="delete"
                  as="button"
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Log out
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  )
}
